import { BRONCO_SUPPLIER_UUID, PRODUCTION_DEMO_CHATS } from '@/mocks/uuids';
import {
  broncoDemoHiddenAlerts,
  dummyHiddenAlerts,
} from '@/hooks/use-dummy-data';
import {
  GetAllHiddenHubAlertsQuery,
  GetAllHiddenHubAlertsQueryVariables,
} from '@/types';
import { gql } from '@apollo/client';
import { parseCardDataJson } from '@/utils/update-alert-cache';
import { partnerSupplier } from '@/utils/environment';
import { useEffect, useMemo, useState } from 'react';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const GET_ALL_HIDDEN_HUB_ALERTS_QUERY = gql`
  query getAllHiddenHubAlerts(
    $businessOrganizationUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getAllHubAlerts(
      business_organization_id: $businessOrganizationUUID
      filters: { alert_statuses: HIDDEN }
      supplier_id: $supplierUUID
    ) {
      card_data_json
    }
  }
`;

export const useDemoHiddenAlertsData = (
  supplierUUID: string,
  assignedChatUUIDs: string[],
) => {
  const [loading, setLoading] = useState(true);
  const chatSet = new Set(assignedChatUUIDs);
  const unfiltered =
    supplierUUID === BRONCO_SUPPLIER_UUID
      ? broncoDemoHiddenAlerts
      : dummyHiddenAlerts;
  const data = unfiltered.filter((card) => chatSet.has(card.chat_uuid));

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return {
    data: loading ? undefined : data,
    error: false,
    loading,
  };
};

const useLiveHiddenAlertsData = (
  businessOrganizationUUID: string,
  supplierUUID: string,
) => {
  const { data, error, loading } = useWrappedQuery<
    GetAllHiddenHubAlertsQuery,
    GetAllHiddenHubAlertsQueryVariables
  >(GET_ALL_HIDDEN_HUB_ALERTS_QUERY, {
    variables: {
      businessOrganizationUUID,
      supplierUUID,
    },
  });

  const dataJson = data?.getAllHubAlerts.card_data_json;

  return {
    data: loading
      ? undefined
      : dataJson
        ? parseCardDataJson(dataJson)
        : undefined,
    error,
    loading,
  };
};

export const useHiddenAlertsData = () => {
  const { chatRestaurantMap, selectedRestaurantRelationship } =
    useUserContext();
  const selectedChatUUID = selectedRestaurantRelationship.chatUUIDs[0];
  const supplierUUID = selectedRestaurantRelationship.supplierUUID;

  const assignedChatUUIDs = useMemo(
    () => Object.keys(chatRestaurantMap),
    [chatRestaurantMap],
  );
  const demoQueryResults = useDemoHiddenAlertsData(
    supplierUUID,
    assignedChatUUIDs,
  );
  const liveQueryResults = useLiveHiddenAlertsData(
    partnerSupplier.businessOrganizationUUID,
    supplierUUID,
  );

  if (PRODUCTION_DEMO_CHATS.some((chatUUID) => chatUUID === selectedChatUUID)) {
    return demoQueryResults;
  }

  return liveQueryResults;
};
